import React, { useState, useEffect } from "react";
import StackedBarChart from "./StackedBarChart";
// import AssessmentGauge from "./AssessmentGauge";
import DashboardTable from "./DashboardTable";
import CandidateGaugePanel from "./CandidateGaugePanel";
import TrendLineChart from "./TrendLineChart";
import RecentActivity from "./RecentActivity";
import { useNavigate } from "react-router-dom";
import {
  fetchDashboardStatsService,
  fetchSubscriptionUsageService,
} from '../../Services/dashboard.service';
import { useDispatch } from "react-redux";
import { setStatusFilter } from "../../store/slices/viewCreatedJdsSlice";
// The full plan grid moved to the Usage page; the dashboard keeps only the
// trial countdown, and paid orgs see nothing here at all.
import TrialBanner from "./TrialBanner";
import {
  Eye,
  CirclePause
} from "lucide-react";

const Dashboard = () => {
  const [dashboardStatsV2, setDashboardStatsV2] = useState(null);
  const [dashboardStatsV2Loading, setDashboardStatsV2Loading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchAllDashboardData = async () => {
      try {
        setDashboardStatsV2Loading(true);
        setSubscriptionLoading(true);

        const statsData = await fetchDashboardStatsService();
        setDashboardStatsV2(statsData);

        if (statsData?.org_id) {
          const subData = await fetchSubscriptionUsageService(statsData.org_id);
          setSubscriptionData(subData);
        }
      } catch (error) {
        console.error("Failed to load dashboard data", error);
      } finally {
        setDashboardStatsV2Loading(false);
        setSubscriptionLoading(false);
      }
    };

    fetchAllDashboardData();
  }, []);

  const JobCards = ({ icon, title, text, status }) => {
    const handleCardClick = () => {
      if (status && text > 0) {
        dispatch(setStatusFilter(status));
        navigate("/org-admin/created-jds");
      }
    };

    return (
      <div
        onClick={handleCardClick}
        className={`w-full dark:bg-gradient-to-r dark:from-[#232142]/10 dark:to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex flex-col transition-all ${
          text > 0 ? "cursor-pointer hover:scale-[1.02] active:scale-95" : "cursor-default"
        }`}
      >
        <div className="m-4 font-ligh gap-2 items-center space-y-2">
          <div className="flex space-x-4 font-semibold dark:text-[#AEB9E1] text-[#404040]">
            <span>{icon}</span>
            <span>{title}</span>
          </div>
          <div className="font-bold text-4xl dark:text-[#FFFFFF] text-[#707070]">
            <span>{text}</span>
          </div>
        </div>
      </div>
    );
  };

  const metrics = dashboardStatsV2;
  const rawJobMetrics = metrics?.job_metrics || [];

  // Normalize job metrics to handle both flat and nested structures
  const jobMetrics = rawJobMetrics.map((item) => ({
    job_id: item.job_id || item.id || null,
    job_role: item.job_role || item.role || "Unknown",
    sourced: item.sourced ?? item.applications?.sourced ?? 0,
    uploads: item.uploads ?? item.applications?.uploads ?? 0,
    pending: item.pending ?? item.pipeline?.pending ?? 0,
    in_progress: item.in_progress ?? item.pipeline?.in_progress ?? 0,
    completed: item.completed ?? item.pipeline?.completed ?? 0,
    selected: item.selected ?? item.pipeline?.selected ?? 0,
    rejected: item.rejected ?? item.pipeline?.rejected ?? 0,
  }));

  // Normalize candidate_summary to handle same structure
  const candidateSummary = metrics?.candidate_summary ? {
    pending: metrics.candidate_summary.pending ?? metrics.candidate_summary.pipeline?.pending ?? 0,
    in_progress: metrics.candidate_summary.in_progress ?? metrics.candidate_summary.pipeline?.in_progress ?? 0,
    completed: metrics.candidate_summary.completed ?? metrics.candidate_summary.pipeline?.completed ?? 0,
    selected: metrics.candidate_summary.selected ?? metrics.candidate_summary.pipeline?.selected ?? 0,
    rejected: metrics.candidate_summary.rejected ?? metrics.candidate_summary.pipeline?.rejected ?? 0,
  } : {};

  // Extract job titles and data from job_metrics array
  const xAxisData = jobMetrics.map((item) => item.job_role);
  const sourcedData = jobMetrics.map((item) => item.sourced);
  const uploadsData = jobMetrics.map((item) => item.uploads);

  // Prepare jobRoles object for CandidateGaugePanel (expects object keyed by role name)
  const jobRolesData = jobMetrics.reduce((acc, item) => {
    if (item.job_role) {
      acc[item.job_role] = item;
    }
    return acc;
  }, {});

  // Final chart data
  const stackedBarChartData = {
    title: "Application per Job Role",
    xAxisData,
    yAxisData: [
      {
        name: "Sourced",
        data: sourcedData,
      },
      {
        name: "Uploads",
        data: uploadsData,
      },
    ],
    barColors: ["#4D70EE", "#A471F3"],
  };

  const handleGaugeClick = (roleName, label) => {
    const tabMap = {
      "Scheduled": "SCHEDULED",
      "In Progress": "PROGRESS",
      "Completed": "COMPLETED"
    };
    const targetTab = tabMap[label];
    if (!targetTab) return;

    const dispatchAction = async () => {
      const {
        updateCurrentJobRole,
        updateCurrentJobId,
        triggerApply,
      } = await import("../../store/slices/filterOptionsSlice");
      const {
        updateActiveTab,
        updateActiveSubTab,
      } = await import("../../store/slices/candidateManagementSlice");
      const { getJobDescriptionThunk } = await import(
        "../../store/thunks/InterviewThunk"
      );

      // 1. Identify Job ID
      let jobId = null;
      if (roleName !== "all") {
        const matchingJob = jobMetrics.find((j) => j.job_role === roleName);
        jobId = matchingJob?.job_id;
      }

      // 2. Set State
      dispatch(updateActiveTab("INTERVIEW"));
      dispatch(updateActiveSubTab(targetTab));
      dispatch(updateCurrentJobRole(roleName === "all" ? "" : roleName));
      dispatch(updateCurrentJobId(roleName === "all" ? null : jobId));

      if (jobId) {
        dispatch(getJobDescriptionThunk(jobId));
      }

      dispatch(triggerApply());
      navigate("/org-admin/candidate-management", {
        state: { fromDashboard: true },
      });
    };
    dispatchAction();
  };

  const handleBarClick = (data) => {
    if (data?.job_role) {
      const dispatchAction = async () => {
        const {
          updateCurrentJobRole,
          updateCurrentJobId,
          triggerApply,
        } = await import("../../store/slices/filterOptionsSlice");
        const { getJobDescriptionThunk } = await import(
          "../../store/thunks/InterviewThunk"
        );

        dispatch(updateCurrentJobRole(data.job_role));
        if (data.job_id) {
          dispatch(updateCurrentJobId(data.job_id));
          dispatch(getJobDescriptionThunk(data.job_id));
        }
        dispatch(triggerApply());
        navigate("/org-admin/candidate-management", {
          state: { fromDashboard: true },
        });
      };
      dispatchAction();
    }
  };

  const headers = [
    { key: "candidateName", label: "Candidate Name" },
    { key: "candidate_email", label: "Email" },
    { key: "jobRole", label: "Job Role" },
    { key: "Score", label: "Score" },
  ];

  const rows = metrics?.top_performers?.map((item, index) => ({
    id: index + 1,
    candidateName: item.name,
    candidate_email: item.candidate_email || "N/A",
    jobRole: item.role,
    Score: `${item.score}%`,
    interview_id: item.interview_id,
  })) || [];

  const DashboardSkeleton = () => {
    const pulse = "animate-pulse bg-gray-200 dark:bg-gray-900 rounded-xl";

    return (
      <div className="min-h-screen flex flex-col w-full p-4 gap-2">
        {/* Subscription pulse */}
        {/* <div className={`w-full h-40 mb-6 ${pulse}`} /> */}
        <div className="flex w-full">
          {/* Left column */}
          <div className="p-4 w-1/2">
            {/* Job cards */}
            <div className="flex py-2 space-x-4">
              <div className={`w-full h-24 ${pulse}`} />
              <div className={`w-full h-24 ${pulse}`} />
            </div>
            {/* Stacked bar chart */}
            <div className="flex-1 py-2">
              <div className={`w-full h-72 ${pulse}`} />
            </div>
            {/* Dashboard table */}
            <div className="flex-1 py-2">
              <div className={`w-full h-52 ${pulse}`} />
            </div>
          </div>

          {/* Right column */}
          <div className="p-4 w-1/2">
            {/* Candidate gauge panel */}
            <div className="flex-1 py-2">
              <div className={`w-full h-64 ${pulse}`} />
            </div>
            {/* Trend line chart */}
            {/* <div className="flex-1 py-2">
            <div className={`w-full h-56 ${pulse}`} />
          </div> */}
            {/* Recent activity */}
            <div className="flex-1 py-2">
              <div className={`w-full h-48 ${pulse}`} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (dashboardStatsV2Loading || subscriptionLoading) return <DashboardSkeleton />;

  return (
    <div className="min-h-screen flex flex-col w-full p-4 gap-2">
      <TrialBanner
        data={subscriptionData}
        onUpgrade={() => navigate("/org-admin/usage", { state: { from: "banner" } })}
      />
      <div className="flex items-stretch w-full">
        <div className="w-1/2 pr-2 flex flex-col">
          <div className="flex pb-2 space-x-4">
            <JobCards
              title="Open Job Roles"
              text={metrics?.jobs_summary?.open || 0}
              icon={<Eye strokeWidth={1.75} />}
              status="Active"
            />
            <JobCards
              title="On Hold Job Roles"
              text={metrics?.jobs_summary?.on_hold || 0}
              icon={<CirclePause strokeWidth={1.75} />}
              status="On Hold"
            />
          </div>

          <div className="flex-1 py-2">
            <StackedBarChart
              title="Application per Job Role"
              xAxisData={stackedBarChartData.xAxisData}
              yAxisData={stackedBarChartData.yAxisData}
              barColors={stackedBarChartData.barColors}
              onBarClick={handleBarClick}
              jobMetrics={jobMetrics}
            />
          </div>

          {/* <div className="flex-1 py-2">
          <AssessmentGauge
            title="Assessment Health"
            value={metrics?.assessment_health?.overall_rate || 0}
            valueColor="#facc15"
            trackColor="#2e2e2e"
            completionRate={metrics?.assessment_health?.completion_rate || 0}
            passingRate={metrics?.assessment_health?.passing_rate || 0}
            completionColor="#D843F6"
            passingColor="#2B5CE6"
          />
        </div> */}

          {/* <div className="flex-1 py-2">
          <DashboardTable
            title="Top Performer"
            headers={headers}
            rows={rows}
          />
        </div> */}

          <div className="flex-1 py-2">
            <RecentActivity
              title="Recent Activity"
              activities={metrics?.recent_activity || []}
            />
          </div>
        </div>

        <div className="pl-2 w-1/2 flex flex-col">
          <div className="pb-2">
            <CandidateGaugePanel
              title="Interview Management"
              overallStats={candidateSummary}
              jobRoles={jobRolesData}
              colors={{
                total: "#C855F7",
                scheduled: "#C855F7",
                pending: "#2B5CE6",
                completed: "#1EC9FF",
                actions_awaiting: "#123A66",
                selected: "#10B981", // New color for Selected
                rejected: "#EF4444", // New color for Rejected
              }}
              onGaugeClick={handleGaugeClick}
            />
          </div>
          {/* <div className="flex-1 py-2">
          <TrendLineChart
            title="Qualified Candidates"
            data={metrics?.qualified_daily_trend || metrics?.qualified_trend}
            lineColors={["#DD8CE9", "#A471F3"]}
          />
        </div> */}

          <div className="flex-1 py-2">
            <DashboardTable
              title="Top Performer"
              headers={headers}
              rows={rows}
            />
          </div>

          {/* <div className="flex-1 py-2">
          <RecentActivity
            title="Recent Activity"
            activities={metrics?.recent_activity || []}
          />
        </div> */}
        </div>
      </div>
    </div>

  );
};

export default Dashboard;